import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { changePassword } from '@/features/auth/api';
import { ApiError } from '@/lib/apiClient';
import { Sheet } from '@/components/ui/Sheet';

interface Props {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}

/** Change-your-own-código flow. Four digits, three fields, checked client-side first. */
export function CodigoSheet({ open, onClose, onDone }: Props) {
  const { t } = useTranslation();
  const [atual, setAtual] = useState('');
  const [novo, setNovo] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAtual('');
    setNovo('');
    setConfirmar('');
    setErro(null);
  }, [open]);

  const campos = [
    { label: t('sheet.codigoAtual'), value: atual, set: setAtual },
    { label: t('sheet.codigoNovo'), value: novo, set: setNovo },
    { label: t('sheet.codigoConfirmar'), value: confirmar, set: setConfirmar },
  ];

  const submit = async () => {
    if (atual.length < 4 || novo.length < 4 || confirmar.length < 4) {
      setErro(t('sheet.codigoCamposInvalidos'));
      return;
    }
    if (novo !== confirmar) {
      setErro(t('sheet.codigoNaoCoincide'));
      return;
    }
    if (novo === atual) {
      setErro(t('sheet.codigoIgual'));
      return;
    }

    setBusy(true);
    setErro(null);
    try {
      await changePassword(atual, novo);
      onDone();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t('sheet.alterarCodigo')}
      subtitle={t('sheet.alterarCodigoLead')}
    >
      <div className="flex flex-col gap-3">
        {campos.map((campo) => (
          <div key={campo.label}>
            <div className="label-eyebrow">{campo.label}</div>
            <input
              type="password"
              inputMode="numeric"
              autoComplete="off"
              placeholder="••••"
              className="field num mt-2 bg-[#fbfbfd] text-[16px] font-semibold tracking-[0.3em]"
              value={campo.value}
              onChange={(e) => {
                campo.set(e.target.value.replace(/\D/g, '').slice(0, 6));
                setErro(null);
              }}
            />
          </div>
        ))}
      </div>

      {erro && (
        <div className="mt-3.5 rounded-xl border border-danger-border bg-danger-bg px-3.5 py-3 text-[12px] font-medium leading-snug text-danger-text">
          {erro}
        </div>
      )}

      <button type="button" disabled={busy} onClick={() => void submit()} className="btn-primary mt-5">
        {t('sheet.guardarCodigo')}
      </button>
    </Sheet>
  );
}
